import { Box, PageHeader } from '@dagens/carrot';
import { useTranslation } from '@dagens/frontend-i18n';
import { addMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '../../../_common/hooks/reduxHooks';
import { getOrderHistory } from '../../../api';
import { ContentLoader } from '../../../components/content-loader';
import { getDateShort } from '../../../utils/date/format';
import REQ, { ReqType } from '../../../utils/REQ';
import { SettlementContainer } from './SettlementContainer';
import Callout from '@carrot-deprecated/callout';
import Typography from '@carrot-deprecated/typography';
import Container from '@carrot-deprecated/container';
import SearchInput from '@components/search-input/SearchInput';
import { OrderKind } from 'types/Order';
import { Page } from '@components/page';
import { orderKindIsOrder, ordersBySettlement } from '_common/reducers/orders';
import { useQueryParams } from '_common/hooks/useQueryParams';

type SettlementListProps = {
  searchQuery: string | null;
};

const SettlementsList = ({ searchQuery }: SettlementListProps) => {
  const { t } = useTranslation();
  const [req, setReq] = useState<ReqType>(REQ.INIT);
  const [orders, setOrders] = useState<OrderKind[]>([]);
  const { roleId } = useAppSelector(({ auth }) => {
    return {
      roleId: auth._id
    };
  });

  const fetchOrderHistory = async () => {
    setReq(REQ.PENDING);

    const FETCH_PERIOD = 24;
    const FETCH_INTERVAL = 3;

    const today = new Date();
    const history = await Promise.all(
      Array.from({ length: FETCH_PERIOD / FETCH_INTERVAL }).map(
        async (_, i) => {
          const startDate = addMonths(
            today,
            -(i + 1) * FETCH_INTERVAL
          ).toISOString();
          const endDate = addMonths(today, -i * FETCH_INTERVAL).toISOString();
          const orderHistory = await getOrderHistory({
            producerId: roleId,
            startDate,
            endDate
          });
          return orderHistory;
        }
      )
    );

    setOrders(history.flat());
    setReq(REQ.SUCCESS);
  };

  const settlements = useMemo(() => {
    const filteredOrders = orders.filter(order => {
      if (!searchQuery || searchQuery.length < 1) {
        return true;
      }
      const orderSearchableFields = [
        order.orderNumberString,
        order.settlement?.settlementNumber,
        order.consumer.name,
        orderKindIsOrder(order)
          ? getDateShort(order.deliveryDate)
          : getDateShort(order._createdAt)
      ].filter(Boolean);
      const searchKeywords = searchQuery.toLowerCase().split(' ');

      return searchKeywords.every(keyword => {
        return orderSearchableFields.some(field => {
          return field?.toLowerCase().includes(keyword);
        });
      });
    });

    return ordersBySettlement(filteredOrders);
  }, [orders, searchQuery]);

  useEffect(() => {
    fetchOrderHistory();
  }, []);

  return (
    <ContentLoader req={req}>
      <Container mt="m">
        {settlements.length < 1 && (
          <Callout>
            <Typography variant="paragraph">
              {t('common:NoOrderHistoryYet')}
            </Typography>
          </Callout>
        )}
        {settlements.sort().map(settlement => {
          return (
            <SettlementContainer key={settlement.key} settlement={settlement} />
          );
        })}
      </Container>
    </ContentLoader>
  );
};

const QUERY_KEY = 'query';

const ProducerOrdersHistoryPage = () => {
  const { getQueryValue, setQueryValue } = useQueryParams();
  const searchQuery = getQueryValue(QUERY_KEY);
  const { t } = useTranslation();

  return (
    <Page
      header={
        <Box.FullWidth>
          <PageHeader
            title={t('common:OrderHistory')}
            subtitle={t('producer:OrderHistoryPageDescription')}
          />
          <SearchInput
            searchPlaceholder={t('common:SearchInOrderHistory')}
            searchStringInput={searchQuery || ''}
            onSearchChange={({ target: { value } }) => {
              return setQueryValue(QUERY_KEY, value);
            }}
            onResetSearchString={() => {
              return setQueryValue(QUERY_KEY, '');
            }}
          />
        </Box.FullWidth>
      }
    >
      <SettlementsList searchQuery={searchQuery} />
    </Page>
  );
};

export default ProducerOrdersHistoryPage;
